import * as THREE from 'three';

const MOBILE_TAP_RADIUS = 0.08; // NDC units (~4% screen width — matches original)

// Drag detection: if pointer moved more than this, treat as camera orbit, not a click
const DRAG_THRESHOLD_PX = 8;
// Long-press detection: if pointer was held longer than this, treat as camera orbit
const LONG_PRESS_MS = 300;

const PORTAL_POS = new THREE.Vector3(250, 50, 280);
const PORTAL_HIT_RADIUS = 15;

const isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  window.innerWidth <= 768;

interface RaycasterCallbacks {
  onStarClick: (wallet: string, index: number, x: number, y: number) => void;
  onShipClick: () => void;
  onPortalClick: (distance: number) => void;
  onEmptyClick: () => void;
}

/**
 * Click/touch detection for stars, ship, portal.
 * Ported from: useRaycaster.ts
 */
export class RaycasterManager {
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private callbacks: RaycasterCallbacks;

  // External state that must be set by SpaceEngine
  private points: THREE.Points | null = null;
  private ship: THREE.Group | null = null;
  private wallets: string[] = [];
  private followMode = false;

  private handleClick: (e: MouseEvent) => void;
  private handleTouchEnd: (e: TouchEvent) => void;
  private handlePointerDown: (e: PointerEvent) => void;
  private handleTouchStart: (e: TouchEvent) => void;

  // Drag detection state
  private pointerDown = { x: 0, y: 0, time: 0 };
  private touchStart = { x: 0, y: 0, time: 0 };

  constructor(
    private container: HTMLDivElement,
    private camera: THREE.PerspectiveCamera,
    callbacks: RaycasterCallbacks
  ) {
    this.callbacks = callbacks;

    this.handleClick = this.onClick.bind(this);
    this.handleTouchEnd = this.onTouchEnd.bind(this);
    this.handlePointerDown = (e: PointerEvent) => {
      this.pointerDown = { x: e.clientX, y: e.clientY, time: Date.now() };
    };
    this.handleTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) this.touchStart = { x: t.clientX, y: t.clientY, time: Date.now() };
    };

    container.addEventListener('pointerdown', this.handlePointerDown);
    container.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    container.addEventListener('click', this.handleClick);
    container.addEventListener('touchend', this.handleTouchEnd);
  }

  setPoints(points: THREE.Points | null): void {
    this.points = points;
  }

  setWallets(wallets: string[]): void {
    this.wallets = wallets;
  }

  setShip(ship: THREE.Group | null): void {
    this.ship = ship;
  }

  setFollowMode(followMode: boolean): void {
    this.followMode = followMode;
  }

  private onClick(event: MouseEvent): void {
    if (!this.points) return;

    // Drag/long-press guard: don't open panel if user was orbiting the camera
    const dragDist = Math.sqrt(
      (event.clientX - this.pointerDown.x) ** 2 + (event.clientY - this.pointerDown.y) ** 2
    );
    if (dragDist > DRAG_THRESHOLD_PX || Date.now() - this.pointerDown.time > LONG_PRESS_MS) return;

    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Dynamic threshold: bigger on mobile, scales with camera distance
    const camDist = this.camera.position.length();
    const baseThreshold = isMobile ? 2.5 : 0.8;
    this.raycaster.params.Points = {
      threshold: baseThreshold * Math.max(1, camDist / 80),
    };
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Check spaceship click first
    if (this.ship) {
      if (this.checkShipHit()) return;
    }

    // Check portal click
    if (this.checkPortalHit()) return;

    // Check star clicks
    const intersects = this.raycaster.intersectObject(this.points);
    if (intersects.length > 0) {
      const index = intersects[0].index;
      if (index !== undefined && this.wallets[index]) {
        this.callbacks.onStarClick(this.wallets[index], index, event.clientX, event.clientY);
        return;
      }
    }

    this.callbacks.onEmptyClick();
  }

  private onTouchEnd(e: TouchEvent): void {
    if (!this.points) return;

    const touch = e.changedTouches[0];
    if (!touch) return;
    // Skip if multi-touch
    if (e.touches.length > 0) return;

    const cx = touch.clientX;
    const cy = touch.clientY;
    const now = Date.now();

    // Drag/long-press guard: don't open panel if user was panning the camera
    const touchDragDist = Math.sqrt((cx - this.touchStart.x) ** 2 + (cy - this.touchStart.y) ** 2);
    const touchDuration = now - this.touchStart.time;
    if (touchDragDist > DRAG_THRESHOLD_PX || touchDuration > LONG_PRESS_MS) return;

    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((cx - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((cy - rect.top) / rect.height) * 2 + 1;

    // Dynamic threshold for raycaster
    const camDist = this.camera.position.length();
    const baseThreshold = 2.5; // always mobile threshold for touch
    this.raycaster.params.Points = {
      threshold: baseThreshold * Math.max(1, camDist / 80),
    };
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Single tap — portal, ship, or star click
    if (this.checkPortalHit()) return;

    if (this.ship && !this.followMode) {
      const shipObjs: THREE.Object3D[] = [];
      this.ship.traverse((c) => {
        if ((c as THREE.Mesh).isMesh) shipObjs.push(c);
      });
      if (this.raycaster.intersectObjects(shipObjs, true).length > 0) {
        const distToShip = this.camera.position.distanceTo(this.ship.position);
        if (distToShip >= 80) {
          this.callbacks.onShipClick();
        }
        return;
      }
    }

    // Raycaster hit
    let clickedIdx = -1;
    const hits = this.raycaster.intersectObject(this.points);
    if (hits.length > 0 && hits[0].index !== undefined) {
      clickedIdx = hits[0].index;
    } else {
      // Screen-space fallback: find nearest star to tap point (matches original mobile behavior)
      const positions = this.points.geometry.attributes.position.array as Float32Array;
      let minDist = MOBILE_TAP_RADIUS;
      const starWorld = new THREE.Vector3();
      const count = positions.length / 3;
      for (let i = 0; i < count; i++) {
        starWorld.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
        this.points.localToWorld(starWorld);
        const projected = starWorld.project(this.camera);
        if (projected.z > 1) continue; // behind camera
        const dx = projected.x - this.mouse.x;
        const dy = projected.y - this.mouse.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < minDist) {
          minDist = d;
          clickedIdx = i;
        }
      }
    }

    if (clickedIdx >= 0 && this.wallets[clickedIdx]) {
      // Single tap on star always fires — handler exits follow mode if needed (matches original)
      this.callbacks.onStarClick(this.wallets[clickedIdx], clickedIdx, cx, cy);
    } else {
      // Single tap on empty space — close panel (matches original)
      this.callbacks.onEmptyClick();
    }
  }

  private checkShipHit(): boolean {
    if (!this.ship) return false;

    const shipObjects: THREE.Object3D[] = [];
    this.ship.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        shipObjects.push(child);
      }
    });

    const shipIntersects = this.raycaster.intersectObjects(shipObjects, true);
    const shipScreenPos = this.ship.position.clone().project(this.camera);
    const clickDist = Math.sqrt(
      (shipScreenPos.x - this.mouse.x) ** 2 + (shipScreenPos.y - this.mouse.y) ** 2
    );

    if (shipIntersects.length > 0 || (shipScreenPos.z < 1 && clickDist < 0.15)) {
      if (this.followMode) return true;
      const distToShip = this.camera.position.distanceTo(this.ship.position);
      if (distToShip >= 80) {
        this.callbacks.onShipClick();
      }
      return true;
    }
    return false;
  }

  private checkPortalHit(): boolean {
    const toPortal = PORTAL_POS.clone().sub(this.raycaster.ray.origin);
    const tProj = toPortal.dot(this.raycaster.ray.direction);
    if (tProj > 0) {
      const closest = this.raycaster.ray.origin
        .clone()
        .addScaledVector(this.raycaster.ray.direction, tProj);
      if (closest.distanceTo(PORTAL_POS) < PORTAL_HIT_RADIUS) {
        this.callbacks.onPortalClick(this.camera.position.distanceTo(PORTAL_POS));
        return true;
      }
    }
    return false;
  }

  destroy(): void {
    this.container.removeEventListener('pointerdown', this.handlePointerDown);
    this.container.removeEventListener('touchstart', this.handleTouchStart);
    this.container.removeEventListener('click', this.handleClick);
    this.container.removeEventListener('touchend', this.handleTouchEnd);
  }
}
